import type { ICoordinates } from '../../../../../Model/index.js'

export type InvalidLatLonEntry = {
  rowIndex: number
  lat?: string
  lon?: string
  rawValue?: string // for combined format
}

export function findInvalidLatLonValues(
  rawCSVData: Record<string, string>[],
  mode: 'split' | 'single',
  latCol?: string,
  lonCol?: string,
  coordinatesCol?: string
): InvalidLatLonEntry[] {
  const invalids: InvalidLatLonEntry[] = []

  rawCSVData.forEach((row, index) => {
    if (mode === 'split') {
      const latStr = latCol ? row[latCol]?.replace(',', '.').trim() : undefined
      const lonStr = lonCol ? row[lonCol]?.replace(',', '.').trim() : undefined

      const isEmptyLat = !latStr
      const isEmptyLon = !lonStr

      if (isEmptyLat && isEmptyLon) {
        return // skip empty entries
      }

      const lat = latStr !== undefined ? parseFloatStrict(latStr) : NaN
      const lon = lonStr !== undefined ? parseFloatStrict(lonStr) : NaN

      const isValidLat = !isNaN(lat) && lat >= -90 && lat <= 90
      const isValidLon = !isNaN(lon) && lon >= -180 && lon <= 180

      if (!isValidLat || !isValidLon) {
        invalids.push({ rowIndex: index, lat: latStr, lon: lonStr })
      }
    } else if (mode === 'single') {
      const rawValue = coordinatesCol ? row[coordinatesCol]?.trim() : ''
      if (!rawValue) {
        return
      }
      const [latStr, lonStr] = rawValue?.split(',').map((s) => s?.trim().replace(',', '.')) ?? []

      const lat = latStr !== undefined ? parseFloatStrict(latStr) : NaN
      const lon = lonStr !== undefined ? parseFloatStrict(lonStr) : NaN

      const isValidLat = !isNaN(lat) && lat >= -90 && lat <= 90
      const isValidLon = !isNaN(lon) && lon >= -180 && lon <= 180

      if (!isValidLat || !isValidLon) {
        invalids.push({ rowIndex: index, lat: latStr, lon: lonStr, rawValue })
      }
    }
  })

  return invalids
}

const numberOrUndefined = (s: string | undefined) => {
  if (!s) {
    return undefined
  }
  const num = Number(s.replace(',', '.'))
  return isNaN(num) ? undefined : num
}

/** A merged cell reads "lat,lon", as the import modal labels it; the result is GeoJSON, i.e. [lon, lat]. */
export function sampleCoordinatesFromRow(
  row: Record<string, string>,
  mode: 'split' | 'single',
  latCol?: string,
  lonCol?: string,
  coordinatesCol?: string
): ICoordinates | undefined {
  let lat: number | undefined
  let lon: number | undefined
  if (mode === 'single') {
    if (!coordinatesCol || typeof row[coordinatesCol] !== 'string') {
      return undefined
    }
    const [latPart, lonPart] = row[coordinatesCol].split(',')
    lat = Number(latPart)
    lon = Number(lonPart)
  } else if (latCol && lonCol) {
    lat = numberOrUndefined(row[latCol])
    lon = numberOrUndefined(row[lonCol])
  }
  if (lat === undefined || lon === undefined || isNaN(lat) || isNaN(lon)) {
    return undefined
  }
  /* 0,0 is a placeholder (or an empty single cell), not a sampling site. */
  if (lat === 0 && lon === 0) {
    return undefined
  }
  return { type: 'Point', coordinates: [lon, lat] }
}

function parseFloatStrict(value: string): number {
  if (isStrictFloat(value)) {
    return parseFloat(value)
  } else {
    return NaN
  }
}

/* javascript is quite lenient when parsing floats, we do not like this */
function isStrictFloat(value: string): boolean {
  return /^-?\d+(\.\d+)?$/.test(value)
}
